import React, { useEffect, useMemo, useState } from "react";
import { Card } from "react-bootstrap";
import Chart from "react-apexcharts";
import { fetchIncomeStatementData } from "../../apis/Stock";
import useDarkMode from "../../hooks/useDarkMode";
import { getChartTheme } from "../../utils/chartTheme";

const RND_RATIO_COLORS = ["#00a86b", "#ff8c00"];

const buildPeriodLabel = (value, period) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value ?? "");

  if (period === "annual") {
    return String(date.getFullYear());
  }

  const year = String(date.getFullYear()).slice(2);
  const quarter = Math.ceil((date.getMonth() + 1) / 3);
  return `${year}Q${quarter}`;
};

const toNumberOrNull = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const toRatioPercent = (numerator, denominator) => {
  const numericNumerator = toNumberOrNull(numerator);
  const numericDenominator = toNumberOrNull(denominator);

  if (numericNumerator === null || numericDenominator === null || numericDenominator === 0) {
    return null;
  }

  return (numericNumerator / numericDenominator) * 100;
};

// 매출액 대비 RnD 비율 차트
export default function RnDRatioChart({ ticker, selectedPeriod = "quarter" }) {
  const [chartDataByPeriod, setChartDataByPeriod] = useState({ quarter: null, annual: null });
  const [isLoading, setIsLoading] = useState(false);
  const isDarkMode = useDarkMode();
  const chartColors = useMemo(() => getChartTheme(isDarkMode), [isDarkMode]);

  useEffect(() => {
    if (!ticker) {
      setChartDataByPeriod({ quarter: null, annual: null });
      setIsLoading(false);
      return;
    }

    setChartDataByPeriod({ quarter: null, annual: null });
  }, [ticker]);

  useEffect(() => {
    if (!ticker) {
      return;
    }

    if (chartDataByPeriod[selectedPeriod] !== null) {
      return;
    }

    let isMounted = true;

    async function fetchData() {
      setIsLoading(true);

      try {
        const response = await fetchIncomeStatementData(ticker, selectedPeriod);

        if (!response.ok) {
          throw new Error("Failed to fetch income statement");
        }

        const data = await response.json();
        if (!isMounted) return;

        setChartDataByPeriod((prev) => ({
          ...prev,
          [selectedPeriod]: Array.isArray(data) ? data : [],
        }));
      } catch (error) {
        console.error("Error fetching income statement:", error);
        if (isMounted) {
          setChartDataByPeriod((prev) => ({
            ...prev,
            [selectedPeriod]: [],
          }));
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [chartDataByPeriod, selectedPeriod, ticker]);

  const rows = useMemo(
    () => (Array.isArray(chartDataByPeriod[selectedPeriod]) ? [...chartDataByPeriod[selectedPeriod]] : [])
      .filter((item) => item?.date && toNumberOrNull(item?.revenue) !== null)
      .sort((a, b) => new Date(a.date) - new Date(b.date)),
    [chartDataByPeriod, selectedPeriod]
  );

  const categories = useMemo(
    () => rows.map((item, index) => {
      const date = new Date(item.date);
      if (Number.isNaN(date.getTime())) return buildPeriodLabel(item.date, selectedPeriod);

      const currentYear = date.getFullYear();
      if (selectedPeriod === "annual") {
        return String(currentYear);
      }

      const previousRow = index > 0 ? rows[index - 1] : null;
      const previousYear = previousRow?.date ? new Date(previousRow.date).getFullYear() : null;
      return currentYear !== previousYear ? String(currentYear) : "";
    }),
    [rows, selectedPeriod]
  );

  const series = useMemo(
    () => [
      {
        name: "R&D",
        type: "bar",
        data: rows.map((item) => toNumberOrNull(item?.researchAndDevelopmentExpenses)),
      },
      {
        name: "R&D 비율",
        type: "line",
        data: rows.map((item) => toRatioPercent(item?.researchAndDevelopmentExpenses, item?.revenue)),
      },
    ],
    [rows]
  );

  const hasData = useMemo(
    () => series.some((entry) => entry.data.some((value) => value !== null)),
    [series]
  );

  const options = useMemo(
    () => ({
      chart: {
        type: "line",
        stacked: false,
        toolbar: { show: false },
        zoom: { enabled: false },
        selection: { enabled: false },
        animations: { enabled: false },
        background: chartColors.panelBg,
      },
      responsive: [
        {
          breakpoint: 576,
          options: {
            xaxis: {
              labels: {
                rotate: -90,
                rotateAlways: true,
              },
            },
          },
        },
      ],
      colors: RND_RATIO_COLORS,
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "54%",
          borderRadius: 4,
          borderRadiusApplication: "end",
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: "straight",
        width: [0, 2.5],
      },
      markers: {
        size: [0, 3],
        strokeWidth: 0,
        strokeColors: "transparent",
        fillOpacity: 1,
        hover: {
          size: 4,
        },
      },
      fill: {
        opacity: [0.92, 1],
      },
      legend: {
        position: "bottom",
        horizontalAlign: "center",
        floating: false,
        clusterGroupedSeries: false,
        labels: {
          colors: chartColors.legendColor,
        },
        itemMargin: {
          horizontal: 8,
          vertical: 0,
        },
      },
      title: {
        text: "R&D 투자 비율 추이",
        align: "center",
        offsetY: 10,
        style: {
          color: chartColors.legendColor,
          fontSize: "18px",
          fontWeight: 700,
        },
      },
      grid: {
        borderColor: chartColors.gridColor,
        strokeDashArray: 4,
        padding: {
          top: 2,
          bottom: 2,
        },
      },
      xaxis: {
        categories,
        labels: {
          style: {
            colors: chartColors.tickColor,
            fontSize: "11px",
          },
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: [
        {
          seriesName: "R&D",
          labels: {
            style: {
              colors: chartColors.tickColor,
              fontSize: "11px",
            },
            formatter: (value) => {
              const numeric = Number(value);
              if (!Number.isFinite(numeric)) return "-";

              const absValue = Math.abs(numeric);
              const sign = numeric < 0 ? "-" : "";

              if (absValue >= 1_000_000_000) {
                return `${sign}$${(absValue / 1_000_000_000).toFixed(1).replace(/\.0$/, "")}B`;
              }
              if (absValue >= 1_000_000) {
                return `${sign}$${(absValue / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
              }
              if (absValue >= 1_000) {
                return `${sign}$${(absValue / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
              }

              return `${sign}$${absValue.toFixed(0)}`;
            },
          },
        },
        {
          seriesName: "R&D 비율",
          opposite: true,
          labels: {
            minWidth: 34,
            maxWidth: 34,
            offsetX: -6,
            style: {
              colors: chartColors.tickColor,
              fontSize: "11px",
            },
            formatter: (value) => {
              const numeric = Number(value);
              return Number.isFinite(numeric) ? `${numeric.toFixed(0)}%` : "-";
            },
          },
        },
      ],
      tooltip: {
        theme: isDarkMode ? "dark" : "light",
        shared: true,
        intersect: false,
        x: {
          formatter: (_, context) => rows?.[context.dataPointIndex]?.date || "",
        },
        y: {
          formatter: (value, context) => {
            const numeric = Number(value);

            if (context.seriesIndex === 1) {
              return Number.isFinite(numeric) ? `${numeric.toFixed(2)}%` : "-";
            }

            if (!Number.isFinite(numeric)) return "-";

            const absValue = Math.abs(numeric);
            const sign = numeric < 0 ? "-" : "";

            if (absValue >= 1_000_000_000) {
              return `${sign}$${(absValue / 1_000_000_000).toFixed(1).replace(/\.0$/, "")}B`;
            }
            if (absValue >= 1_000_000) {
              return `${sign}$${(absValue / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
            }
            if (absValue >= 1_000) {
              return `${sign}$${(absValue / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
            }

            return `${sign}$${absValue.toFixed(0)}`;
          },
        },
      },
      noData: {
        text: "데이터가 없습니다.",
        align: "center",
        verticalAlign: "middle",
        style: {
          color: chartColors.tickColor,
        },
      },
    }),
    [categories, chartColors, isDarkMode, rows]
  );

  if (isLoading) {
    return (
      <div
        className="d-flex flex-column align-items-center justify-content-center h-100"
        style={{ minHeight: "300px", backgroundColor: chartColors.panelBg, borderRadius: "12px", color: chartColors.tickColor }}
      >
        <div className="spinner-border spinner-border-sm text-primary mb-2" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <div style={{ fontSize: "13px" }}>데이터를 불러오는 중입니다...</div>
      </div>
    );
  }

  if (!hasData) {
    return (
      <div
        className="d-flex align-items-center justify-content-center h-100"
        style={{ minHeight: "300px", backgroundColor: chartColors.panelBg, borderColor: chartColors.panelBorder }}
      >
        데이터가 없습니다.
      </div>
    );
  }

  return (
    <Card
      className="w-full h-full"
      style={{ minHeight: "300px", backgroundColor: chartColors.panelBg, borderColor: chartColors.panelBorder }}
    >
      <div className="rnd-ratio-chart">
        <style>
          {`
            .rnd-ratio-chart .apexcharts-legend {
              display: flex !important;
              flex-wrap: nowrap !important;
              justify-content: center;
              gap: 0 !important;
            }

            .rnd-ratio-chart .apexcharts-legend-series {
              display: inline-flex !important;
              align-items: center;
              white-space: nowrap !important;
            }
          `}
        </style>
        <Chart options={options} series={series} type="line" height={300} />
      </div>
    </Card>
  );
}
